import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { getPublicJob } from '../../../services/jobs';
import { BaseContainer } from '../../global/Core';

import type { PublicJob } from '../../../types/job';

const StyledCard = styled(Card)`
    max-width: 600px;
    margin: 0 auto;
    padding: 2rem;
    text-align: center;
`;

const JobDetailsSection = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 2rem 0;
    padding: 1.5rem;
    background-color: #f5f5f5;
    border-radius: 8px;
`;

const JobDetailItem = styled(Box)`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    justify-content: center;
`;

const ButtonContainer = styled(Box)`
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
    justify-content: center;

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: center;
    }
`;

const StyledButton = styled(Button)`
    min-width: 140px;
    padding: 12px 24px;
`;

export default function JobQualifyLanding() {
    const { jobId } = useParams<{ jobId: string }>();
    const [job, setJob] = useState<PublicJob | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (jobId) {
            getPublicJob(parseInt(jobId, 10))
                .then((jobData: PublicJob) => {
                    setJob(jobData);
                    setLoading(false);
                    setError(null);
                })
                .catch(() => {
                    setError('Job not found or no longer available');
                    setLoading(false);
                });
        }
    }, [jobId]);

    if (loading) {
        return (
            <BaseContainer>
                <StyledCard>
                    <CardContent>
                        <Typography variant={'h5'}>
                            {'Loading job details...'}
                        </Typography>
                    </CardContent>
                </StyledCard>
            </BaseContainer>
        );
    }

    if (error || !job) {
        return (
            <BaseContainer>
                <StyledCard>
                    <CardContent>
                        <Typography variant={'h5'} color={'error'} gutterBottom>
                            {'Job Not Found'}
                        </Typography>
                        <Typography variant={'body1'} color={'text.secondary'}>
                            {error ||
                                'This job posting is no longer available.'}
                        </Typography>
                    </CardContent>
                </StyledCard>
            </BaseContainer>
        );
    }

    return (
        <BaseContainer>
            <StyledCard>
                <CardContent>
                    <Typography
                        variant={'h4'}
                        component={'h1'}
                        gutterBottom
                        color={'primary'}
                    >
                        {"You've been invited to apply!"}
                    </Typography>

                    <Typography
                        variant={'body1'}
                        color={'text.secondary'}
                        sx={{ mb: 3 }}
                    >
                        {'Someone shared this job opportunity with you. ' +
                            'Sign in or create an account to check your ' +
                            'qualification and book an interview.'}
                    </Typography>

                    <JobDetailsSection>
                        <JobDetailItem>
                            <WorkIcon color={'primary'} />
                            <Typography variant={'h6'} fontWeight={'600'}>
                                {job.title}
                            </Typography>
                        </JobDetailItem>

                        <JobDetailItem>
                            <BusinessIcon color={'secondary'} />
                            <Typography variant={'body1'}>
                                {job.company.name}
                            </Typography>
                        </JobDetailItem>

                        <JobDetailItem>
                            <LocationOnIcon color={'action'} />
                            <Typography
                                variant={'body2'}
                                color={'text.secondary'}
                            >
                                {job.company.location}
                            </Typography>
                        </JobDetailItem>
                    </JobDetailsSection>

                    <Typography
                        variant={'body2'}
                        color={'text.secondary'}
                        sx={{ mb: 2 }}
                    >
                        {'To continue, please sign in to your existing ' +
                            'account or create a new candidate account.'}
                    </Typography>

                    <ButtonContainer>
                        <StyledButton
                            variant={'contained'}
                            color={'primary'}
                            component={Link}
                            to={`/login?redirect=${encodeURIComponent(
                                `/job/${jobId}/qualify`
                            )}`}
                        >
                            {'Sign In'}
                        </StyledButton>

                        <StyledButton
                            variant={'outlined'}
                            color={'primary'}
                            component={Link}
                            to={`/signup/user?redirect=${encodeURIComponent(
                                `/job/${jobId}/qualify`
                            )}`}
                        >
                            {'Create Account'}
                        </StyledButton>
                    </ButtonContainer>

                    <Typography
                        variant={'caption'}
                        color={'text.secondary'}
                        sx={{ mt: 2, display: 'block' }}
                    >
                        {'Already have a business account? '}
                        <Link to={'/login'}>{'Sign in here'}</Link>
                    </Typography>
                </CardContent>
            </StyledCard>
        </BaseContainer>
    );
}
